import { Box, Collapse, IconButton, List, ListItem, ListItemText, Pagination, Typography, useTheme } from '@mui/material'
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined'
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined'
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { PhoneOutlined, EmailOutlined } from '@mui/icons-material'
import { ReactNode, useState } from 'react'
import { AlertDto } from '../../../interfaces/IAlert'
import { CustomerDto, CustomerType, EventDto } from '../../../interfaces/IEvent'

interface Props {
  viewAlert: AlertDto
  viewEvent: EventDto
}

interface CardItem {
  key: string
  primary: string
  servicePointIds?: string[]
  connectionNumbers?: string[]
}

interface CardDef {
  id: string
  icon: ReactNode
  name: string
  items: CardItem[]
  isAllCompany?: boolean
}

function toCustomerItems(customers: CustomerDto[]): CardItem[] {
  return customers.map((c) => {
    const addresses = c.affectedAddresses ?? []
    return {
      key: c.customerNumber,
      primary: c.customerName,
      servicePointIds: addresses.map((a) => a.servicePointId).filter(Boolean),
      connectionNumbers: addresses.flatMap((a) => a.connectionNumbers ?? []).filter(Boolean),
    }
  })
}

function AlertNotifiedCustomersCards({ viewAlert, viewEvent }: Props) {
  const theme = useTheme()
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const [currentPages, setCurrentPages] = useState<Record<string, number>>({})
  const PAGE_SIZE = 5
  const notified = viewAlert.notifiedCustomers

  const customersByType = (type: CustomerType, ids: string[]) =>
    (viewEvent.affectedCustomers ?? []).filter((c) => c.customerType === type && ids.includes(c.customerNumber))

  const cards: CardDef[] = []

  if (notified?.allCompanyCustomers) {
    cards.push({
      id: 'allCompanyCustomers',
      icon: <BusinessOutlinedIcon />,
      name: 'Alle bedriftskunder',
      items: [],
      isAllCompany: true,
    })
  }

  if ((notified?.companyCustomerIds?.length ?? 0) > 0) {
    cards.push({
      id: 'company',
      icon: <BusinessOutlinedIcon />,
      name: 'Bedrift',
      items: toCustomerItems(customersByType(CustomerType.Company, notified?.companyCustomerIds ?? [])),
    })
  }

  if ((notified?.companyWithSlaCustomerIds?.length ?? 0) > 0) {
    cards.push({
      id: 'sla',
      icon: <KeyOutlinedIcon />,
      name: 'SLA',
      items: toCustomerItems(customersByType(CustomerType.CompanyWithSla, notified?.companyWithSlaCustomerIds ?? [])),
    })
  }

  if ((notified?.privateCustomerIds?.length ?? 0) > 0) {
    cards.push({
      id: 'private',
      icon: <HomeOutlinedIcon />,
      name: 'Privat',
      items: toCustomerItems(customersByType(CustomerType.Private, notified?.privateCustomerIds ?? [])),
    })
  }

  if ((viewAlert.phoneNumbers?.length ?? 0) > 0) {
    cards.push({
      id: 'phones',
      icon: <PhoneOutlined />,
      name: 'Telefon',
      items: (viewAlert.phoneNumbers ?? []).map((p) => ({ key: p, primary: p })),
    })
  }

  if ((viewAlert.emailAddresses?.length ?? 0) > 0) {
    cards.push({
      id: 'emails',
      icon: <EmailOutlined />,
      name: 'Epost',
      items: (viewAlert.emailAddresses ?? []).map((e) => ({ key: e, primary: e })),
    })
  }

  if (cards.length === 0) return null

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  return (
    <Box sx={{ width: '100%' }}>
      {cards.map((card) => {
        const isExpanded = expanded.has(card.id)
        return (
          <Box
            key={card.id}
            sx={{
              border: 2,
              borderRadius: '8px',
              borderColor: theme.palette.primary.light,
              overflow: 'hidden',
              mb: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', p: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>{card.icon}</Box>
              <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
                {card.name}
              </Typography>
              <Typography variant="body2" sx={{ mr: 1, minWidth: 70, textAlign: 'right', color: 'text.secondary' }}>
                {card.isAllCompany ? 'Alle' : card.items.length}
              </Typography>
              {!card.isAllCompany && (
                <IconButton size="small" onClick={() => toggle(card.id)}>
                  {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                </IconButton>
              )}
            </Box>
            {!card.isAllCompany &&
              (() => {
                const page = currentPages[card.id] ?? 0
                const totalPages = Math.ceil(card.items.length / PAGE_SIZE)
                const paginatedItems = card.items.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE)
                return (
                  <Collapse in={isExpanded}>
                    <List dense disablePadding>
                      {paginatedItems.map((item) => {
                        const sps = item.servicePointIds ?? []
                        const conns = item.connectionNumbers ?? []
                        const title = [sps.length > 0 ? `LP: ${sps.join(', ')}` : '', conns.length > 0 ? `Samband: ${conns.join(', ')}` : '']
                          .filter(Boolean)
                          .join(' | ')
                        return (
                          <ListItem key={item.key} sx={{ pl: 2, minWidth: 0 }}>
                            <ListItemText
                              sx={{ minWidth: 0, pr: 2 }}
                              primary={item.primary}
                              secondary={
                                sps.length > 0 || conns.length > 0 ? (
                                  <Box component="span" sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }} title={title}>
                                    {sps.length > 0 && (
                                      <Box component="span">
                                        <Box component="span" sx={{ fontWeight: 600, mr: 0.5 }}>
                                          LP:
                                        </Box>
                                        {sps.join(', ')}
                                      </Box>
                                    )}
                                    {conns.length > 0 && (
                                      <Box component="span">
                                        <Box component="span" sx={{ fontWeight: 600, mr: 0.5 }}>
                                          Samband:
                                        </Box>
                                        {conns.join(', ')}
                                      </Box>
                                    )}
                                  </Box>
                                ) : undefined
                              }
                              slotProps={{
                                primary: { variant: 'body2' },
                                secondary: { variant: 'caption', component: 'div' },
                              }}
                            />
                          </ListItem>
                        )
                      })}
                    </List>
                    {totalPages > 1 && (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, py: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, card.items.length)} av {card.items.length}
                        </Typography>
                        <Pagination
                          size="small"
                          count={totalPages}
                          page={page + 1}
                          onChange={(_, p) => setCurrentPages((prev) => ({ ...prev, [card.id]: p - 1 }))}
                        />
                      </Box>
                    )}
                  </Collapse>
                )
              })()}
          </Box>
        )
      })}
    </Box>
  )
}

export default AlertNotifiedCustomersCards
